import { useRouter } from "next/router";
import { type ReactElement, useEffect } from "react";
import UserRole from "@olivahealth/business/src/Core/domain/value-objects/UserRole";
import { useUserData } from "../../../services/contexts/UserDataContext";
import { UserRoleEnum } from "../../../services/contexts/__generated__/UserDataContextQuery.graphql";

interface Props {
  children: ReactElement;
}

/**
 * This middleware is responsible to check if the user completed the onboarding process.
 * If they signed up after we rolled out the feature, they are required to complete it
 * before they can do any other action on the platform.
 */
export default function EmployeeOnboardingMiddleware({
  children,
}: Props): ReactElement {
  const { data, status } = useUserData();
  const { push, pathname, query } = useRouter();

  useEffect(() => {
    if (status !== "success" || !data) {
      return;
    }

    // Early exit if user has completed all onboarding - no checks needed
    if (data.hasCompletedOnboarding && data.hasCompletedWorkOnboarding) {
      return;
    }

    const isEmployee = data?.role?.some((role) =>
      (
        [
          UserRole.EMPLOYEE,
          UserRole.MANAGER,
          UserRole.ORGANISATION_ADMIN,
        ] as UserRoleEnum[]
      ).includes(role as UserRoleEnum),
    );

    // Skip onboarding check for work-only organisations
    const isWorkOnlyOrganisation =
      data.organisation?.isWorkOnlyOrganisation ?? false;
    if (isWorkOnlyOrganisation) {
      const isOnWorkPath = pathname.startsWith("/work");

      // Users only get redirected to work onboarding when on /work paths
      // This allows them to use the rest of the app without completing work onboarding
      if (
        isOnWorkPath &&
        !data.hasCompletedWorkOnboarding &&
        pathname !== "/work/company" &&
        pathname !== "/work/onboarding" &&
        pathname !== "/slack/pair/error" &&
        !("contact-oliva" in query)
      ) {
        push("/work/onboarding");
      }
      return;
    }

    // Determine the correct onboarding path based on the current URL
    // If user is on a /work path, redirect to /work/onboarding
    // Otherwise, redirect to /onboarding
    const isOnWorkPath = pathname.startsWith("/work");
    const onboardingPath = isOnWorkPath ? "/work/onboarding" : "/onboarding";

    if (
      isEmployee &&
      !data.hasCompletedOnboarding &&
      pathname !== "/onboarding" &&
      pathname !== "/work/onboarding"
    ) {
      push(onboardingPath);
    }
  }, [data, push, status, pathname, query]);

  return children;
}
